import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { useGetBuildingsQuery } from '@client/api';
import { Card, EmptyPlaceholder, Pagination, toISODateStringOrUndefined, useRouteDocumentTitle } from '@client/toolkit';

import { BuildingItem } from './components/building-item.component';
import { BuildingsFilter } from './components/buildings-filter.component';
import { BuildingStatisticsHeader } from './components/building-statistics-header.component';
import { BuildingsTableToolbar } from './components/buildings-table-toolbar.component';
import {
  useBuildingsFilterQueryParams,
  useBuildingsSearchQueryParams,
  useBuildingsSortQueryParams,
  useBuildingsTabQueryParams,
} from './hooks/use-buildings-query-params';

import { PortfolioPaths } from '../../portfolio.router';
import { PageListLayout, PageContentAnimation } from '../../../../components/layout';

export function BuildingListRoute() {
  const { t } = useTranslation('rentalObjects');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  useRouteDocumentTitle(t('documentTitle'), PortfolioPaths.BuildingList);

  const [filterQueryParams] = useBuildingsFilterQueryParams();
  const [sortQueryParams] = useBuildingsSortQueryParams();
  const [searchQueryParams] = useBuildingsSearchQueryParams();
  const [tabQueryParams] = useBuildingsTabQueryParams();

  // discussion about pagination in rtk-query (for later) https://github.com/reduxjs/redux-toolkit/discussions/1163

  const pageSize = 50; // temp workaround to just raise the count instead of paging which works for now but is not ideal (also with a lot of data -> performance might get worse)

  const [page, setPage] = useState(0);

  const { data, isFetching, isLoading } = useGetBuildingsQuery(
    {
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
      'type.Contains': tabQueryParams.buildingTypes ?? undefined,
      'location.From.Latitude':
        filterQueryParams.locationFromEnabled && filterQueryParams.locationFrom
          ? filterQueryParams.locationFrom?.coordinate?.latitude
          : undefined,
      'location.From.Longitude':
        filterQueryParams.locationFromEnabled && filterQueryParams.locationFrom
          ? filterQueryParams.locationFrom?.coordinate?.longitude
          : undefined,
      'location.RadiusMetric':
        filterQueryParams.locationFromEnabled && filterQueryParams.locationRadius
          ? filterQueryParams.locationRadius * 1000
          : undefined,
      'totalAreaMetric.From':
        filterQueryParams.totalAreaEnabled && filterQueryParams.totalAreaFrom
          ? filterQueryParams.totalAreaFrom
          : undefined,
      'totalAreaMetric.To':
        filterQueryParams.totalAreaEnabled && filterQueryParams.totalAreaTo ? filterQueryParams.totalAreaTo : undefined,
      'availableAreaMetric.From':
        filterQueryParams.availableAreaEnabled && filterQueryParams.availableAreaFrom
          ? filterQueryParams.availableAreaFrom
          : undefined,
      'availableAreaMetric.To':
        filterQueryParams.availableAreaEnabled && filterQueryParams.availableAreaTo
          ? filterQueryParams.availableAreaTo
          : undefined,
      'available.Equal.Start': filterQueryParams.availableDateEnabled
        ? toISODateStringOrUndefined(filterQueryParams.availableDateFrom)
        : undefined,
      'available.Equal.End': filterQueryParams.availableDateEnabled
        ? toISODateStringOrUndefined(filterQueryParams.availableDateTo)
        : undefined,
      'vacant.Equal.Start': filterQueryParams.vacantDateEnabled
        ? toISODateStringOrUndefined(filterQueryParams.vacantDateFrom)
        : undefined,
      'vacant.Equal.End': filterQueryParams.vacantDateEnabled
        ? toISODateStringOrUndefined(filterQueryParams.vacantDateTo)
        : undefined,
      'constructionYear.From':
        filterQueryParams.constructionYearEnabled && filterQueryParams.constructionYearFrom
          ? filterQueryParams.constructionYearFrom
          : undefined,
      'constructionYear.To':
        filterQueryParams.constructionYearEnabled && filterQueryParams.constructionYearTo
          ? filterQueryParams.constructionYearTo
          : undefined,
      'bedroomCount.From':
        filterQueryParams.bedroomCountEnabled && filterQueryParams.bedroomCountFrom
          ? filterQueryParams.bedroomCountFrom
          : undefined,
      'bedroomCount.To':
        filterQueryParams.bedroomCountEnabled && filterQueryParams.bedroomCountTo
          ? filterQueryParams.bedroomCountTo
          : undefined,
      'bathroomCount.From':
        filterQueryParams.bathroomCountEnabled && filterQueryParams.bathroomCountFrom
          ? filterQueryParams.bathroomCountFrom
          : undefined,
      'bathroomCount.To':
        filterQueryParams.bathroomCountEnabled && filterQueryParams.bathroomCountTo
          ? filterQueryParams.bathroomCountTo
          : undefined,
      'energyLabel.Contains': filterQueryParams.energyLabel ? filterQueryParams.energyLabel : undefined,
      'standard.Contains': filterQueryParams.standard ? filterQueryParams.standard : undefined,
      'tags.Contains': filterQueryParams.tags
        ? (filterQueryParams.tags.filter((x) => x != null) as string[])
        : undefined,
      search: searchQueryParams.buildingsSearch ? searchQueryParams.buildingsSearch : undefined,
      'sort.Property': sortQueryParams.buildingsSortProperty ? sortQueryParams.buildingsSortProperty : undefined,
      'sort.Order': sortQueryParams.buildingsSortOrder ? sortQueryParams.buildingsSortOrder : undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const buildings = useMemo(() => data?.items ?? [], [data?.items]);
  const buildingTotalCount = useMemo(() => data?.totalCount ?? 0, [data?.totalCount]);

  return (
    <PageListLayout breadcrumb={t('overview')}>
      <BuildingsFilter />
      <PageContentAnimation>
        <Card overflowX={false}>
          <BuildingsTableToolbar totalCount={buildingTotalCount} isFetching={isFetching} />

          {!isLoading && buildingTotalCount > 0 && <BuildingStatisticsHeader />}

          {isLoading && Array.from(Array(10).keys()).map((x) => <BuildingItem key={x} isFetching={true} />)}

          {!isLoading && buildingTotalCount <= 0 && (
            <div className="px-4 py-4 [caption-side:bottom]">
              <EmptyPlaceholder>
                {tCommon('noEntityFound', { entity: tGlobal('entity.building', { context: 'plural' }) })}
              </EmptyPlaceholder>
            </div>
          )}

          {!isLoading && buildings.map((x, idx) => <BuildingItem key={x.id} building={x} isFetching={isFetching} />)}

          {!isLoading && (
            <Pagination
              totalCount={buildingTotalCount}
              currentPage={page}
              setPage={setPage}
              pageSize={pageSize}
              visible
            />
          )}
        </Card>
      </PageContentAnimation>

      <Outlet />
    </PageListLayout>
  );
}
